/**
 * "Filter by this value" — the query half.
 *
 * The filter builder is where a query is AUTHORED; "filter by this value" is
 * the other door, and the one with no learning curve: a value somebody is
 * looking at, turned into the condition that would have selected it. Every
 * surface that shows a value can offer it, and all of them go through one
 * callback (the items page's `applyValueFilter`), so the semantics are decided
 * once — here.
 *
 * THE SEMANTICS, once:
 * - A condition is ANDed onto the query already applied: the current filter is
 *   narrowed, never replaced. (Clearing is what the chips are for.)
 * - The same condition twice is once — an exact duplicate is dropped rather
 *   than stacked, so clicking a value twice is harmless.
 * - A comparison REPLACES the one it argues with. Two `status =` conditions on
 *   one field select nothing at all, a dead end the user has no way to read;
 *   the second click means "no, THIS one". Only the comparisons that can
 *   contradict each other replace (`= > >= < <=`); `contains` and `is any of`
 *   accumulate, because narrowing by two words is a real filter.
 *
 * Which value produces which condition is `components/items/itemFilterValues.js`
 * — it needs the display helpers, and this stays pure query machinery.
 */
import { cmp as makeCmp, and as makeAnd, str as makeStr, parse, serialize, systemFieldOf, isBlock, blockOf } from './kql';
import { TAG_FIELD_KEY } from './tagNames';
import { elementGroupIds, expandElementGroups, fieldKindOf, findField, isElementGroupSegment, isNullaryOperator, parseForBuilder, pathKey, topLevelConditionNodes } from './kqlBuilder';
import { normalizeId } from './ids';
import { elementPickOptions, elementRefusalOf } from './elementRefusal';

/**
 * Is this top-level condition on one of the `wanted` spellings of a path? Its
 * own key, or — for an element GROUP (`#a|#b`, any of these cards) — a group
 * holding an element one of the spellings names by id (`#a`): "Filter by
 * this value" on Task's Status takes the place of a group over Task's and
 * Bug's, as it took the place of `status` before ids (the P4 review's R-FV1).
 */
function onWantedPath(node, wanted) {
	if (!node || node.op !== 'cmp' || !Array.isArray(node.path)) return false;
	if (wanted.has(pathKey(node.path, !!node.forceElement, !!node.facet))) return true;
	if (node.facet || node.path.length !== 1 || !isElementGroupSegment(node.path[0])) return false;
	return elementGroupIds(node.path[0]).some(id => wanted.has(`#${id}`));
}

/** Comparisons where a second one on the same field contradicts the first. */
const REPLACEABLE = new Set(['eq', 'gt', 'gte', 'lt', 'lte']);

/** A comparison and the one that undoes it, on the same value. */
const OPPOSITE = { eq: 'ne', ne: 'eq', contains: 'ncontains', ncontains: 'contains' };

/** Identity of a condition, value included — two of these equal means one chip. */
const conditionKey = node => (
	// A repeating group's block (keepr-api KPR-681) is keyed by its own text:
	// its value is a query, which no `=` can hold.
	isBlock(node)
		? `${pathKey(node.path, !!node.forceElement, !!node.facet)}|has|${serialize({ ...node, path: ['x'], forceElement: undefined })}`
		: `${pathKey(node.path, !!node.forceElement, !!node.facet)}|${node.cmp}|${node.value ? serialize(makeCmp(['x'], 'eq', node.value)) : ''}`
);

/**
 * The query with `conditions` (each `{ cmp, value }`, all on one path) ANDed
 * on; canonical text out, or undefined when nothing is left — which is what
 * the URL carries for "no filter".
 *
 * `accumulate` turns the replace off for a field that holds several values
 * at once — an item carries many tags, so "Tag: Urgent" then "Tag: Follow
 * up" is the items carrying both, not the second replacing the first.
 *
 * `replacePath` is the QUICK-FILTER chip's stronger claim: the chip owns the
 * whole path (its ✕ clears every top-level condition on it, and it reads back
 * only the first), so a pick has to take the place of whatever is there —
 * `is any of (Alice, Bob)` and `is not empty` included, which the ordinary
 * same-operator replace leaves alone. Without it, picking Carol on top of a
 * builder-authored `is any of` ANDs a second condition on and the chip goes on
 * showing the old one. The new conditions land where the first replaced one
 * stood, so the chip's filter keeps its place among the others.
 */
export function applyConditionsToQuery(
	query,
	{ path, forceElement = false, facet = false, conditions = [], replacePath = false, accumulate = false, aliases = [] } = {}
) {
	if (!Array.isArray(path) || !path.length) return query || undefined;
	const { ast } = parseForBuilder(query);
	// An element group (`#a = x or #b = x`) is ONE condition on its segment
	// (`#a|#b`), as the rows read it — written out again below.
	const nodes = [...topLevelConditionNodes(ast)];
	// The path, and the other spellings of the same field (`aliases`: a facet
	// named by its card's key, as text written before F3 names it) — a
	// condition on any of them is a condition on this path.
	const wanted = new Set([pathKey(path, forceElement, facet), ...(Array.isArray(aliases) ? aliases : [])]);
	const onSamePath = entry => onWantedPath(entry, wanted);

	// The insertion point, when the whole path is being taken over: where the
	// first condition on it stood.
	let insertAt = -1;
	if (replacePath) {
		const onPath = onSamePath;
		insertAt = nodes.findIndex(onPath);
		for (let i = nodes.length - 1; i >= 0; i -= 1) if (onPath(nodes[i])) nodes.splice(i, 1);
	}

	(conditions || []).forEach(condition => {
		if (!condition || !condition.cmp) return;
		const nullary = isNullaryOperator(condition.cmp);
		if (!nullary && !condition.value) return;
		const node = makeCmp(path, condition.cmp, nullary ? undefined : condition.value, { forceElement, facet });

		const key = conditionKey(node);
		if (nodes.some(entry => entry?.op === 'cmp' && conditionKey(entry) === key)) return;
		// "Is" then "is not" of the SAME value (or the reverse) asks for nothing:
		// the second click means "no, THIS one", as it does for two different values.
		const opposite = OPPOSITE[condition.cmp];
		if (opposite && !nullary) {
			const against = conditionKey({ ...node, cmp: opposite });
			for (let i = nodes.length - 1; i >= 0; i -= 1) {
				if (nodes[i]?.op === 'cmp' && conditionKey(nodes[i]) === against) nodes.splice(i, 1);
			}
		}

		// `accumulate`: a field that holds SEVERAL values at once (tags) —
		// `tags = A` and `tags = B` do not contradict, they ask for both.
		if (REPLACEABLE.has(condition.cmp) && !accumulate) {
			const at = nodes.findIndex(entry => onSamePath(entry) && entry.cmp === condition.cmp);
			if (at >= 0) { nodes[at] = node; return; }
		}
		if (insertAt >= 0) { nodes.splice(insertAt, 0, node); insertAt += 1; return; }
		nodes.push(node);
	});

	return writeConditions(nodes);
}

/** Top-level conditions (stand-ins included) as canonical text — undefined for none. */
function writeConditions(nodes) {
	const written = nodes.map(expandElementGroups);
	if (!written.length) return undefined;
	return serialize(written.length === 1 ? written[0] : makeAnd(written));
}

/**
 * The condition already applied to one path, or null — what a QUICK-FILTER
 * chip reads to know whether it is active and what it is showing. Only the
 * TOP level is considered: a condition nested under an `or` belongs to a query
 * the chip did not write and must not appear to own.
 *
 * `{ index, cmp, value, path }` — `index` is the position in the top-level
 * condition list, the same coordinate `removeConditionsAt` takes; `path` the
 * condition's own spelling of the field.
 */
export function readConditionOnPath(query, path, forceElement = false, facet = false, aliases = []) {
	if (!Array.isArray(path) || !path.length) return null;
	const { ast } = parseForBuilder(query);
	// `aliases`: the field's other spellings (a facet's card key; an element
	// field's ids, `#k7f3q2xa` — kqlBuilder elementIdKeys), as
	// applyConditionsToQuery takes them.
	const wanted = new Set([pathKey(path, forceElement, facet), ...(Array.isArray(aliases) ? aliases : [])]);
	const nodes = topLevelConditionNodes(ast);
	for (let index = 0; index < nodes.length; index += 1) {
		const node = nodes[index];
		if (node?.op !== 'cmp') continue;
		if (!onWantedPath(node, wanted)) continue;
		// `path`: the spelling the condition is written in — an element's id
		// (`#k7f3q2xa`) where a chart's door wrote it, so a chip's pick can
		// write it back the same way.
		return { index, cmp: node.cmp, value: node.value, path: [...node.path] };
	}
	return null;
}

/**
 * The query with every top-level condition on one path removed — a quick
 * chip's ✕, which clears the whole facet rather than one of two conditions
 * the reader cannot tell apart. Canonical text out, undefined when nothing is
 * left (which is what the URL carries for "no filter").
 */
export function clearConditionsOnPath(query, path, forceElement = false, facet = false, aliases = []) {
	if (!Array.isArray(path) || !path.length) return query || undefined;
	const { ast } = parseForBuilder(query);
	const wanted = new Set([pathKey(path, forceElement, facet), ...(Array.isArray(aliases) ? aliases : [])]);
	const kept = topLevelConditionNodes(ast).filter(node => !(
		onWantedPath(node, wanted)
	));
	return writeConditions(kept);
}

/**
 * The card-type filter for an item: `card = <id>`, descendants included. By
 * id, never by key, which moves (deterministic filters, ruling d19).
 */
export function cardFilterFor(card) {
	const id = normalizeId(card?._id);
	if (!id) return null;
	return {
		path: ['card'],
		forceElement: false,
		label: 'Card type',
		valueLabel: card?.label?.singular || card?.name || card?.key || id,
		conditions: [{ cmp: 'eq', value: makeStr(id) }],
	};
}

/** "Status: Open" — one filter in words. */
export const describeValueFilter = filter => (
	filter ? `${filter.label}: ${filter.valueLabel}` : ''
);

/**
 * The query with a TAG NAME replaced by the tag's id, wherever a top-level or
 * nested `tags` comparison holds it (case-insensitively, as the API matches a
 * name): the person picked which of the tags an ambiguous name meant
 * (`ambiguous_tag`, deterministic filters ruling E2). Text that does not parse
 * comes back as given.
 */
export function replaceTagName(query, name, tagId) {
	const text = (query || '').trim();
	const wanted = String(name || '').trim().toLowerCase();
	if (!text || !wanted || !tagId) return query;
	let ast;
	try { ast = parse(text); } catch (err) { return query; }
	const swap = value => (value && value.type === 'str' && String(value.v).trim().toLowerCase() === wanted ? makeStr(String(tagId)) : value);
	const walk = (node) => {
		if (!node || typeof node !== 'object') return node;
		if (Array.isArray(node.args)) return { ...node, args: node.args.map(walk) };
		if (node.arg) return { ...node, arg: walk(node.arg) };
		const isTags = node.op === 'cmp' && !node.facet && !node.forceElement && Array.isArray(node.path) && node.path.length === 1 && node.path[0] === TAG_FIELD_KEY;
		if (!isTags || !node.value) return node;
		if (node.value.type === 'list') return { ...node, value: { ...node.value, v: node.value.v.map(swap) } };
		return { ...node, value: swap(node.value) };
	};
	return serialize(walk(ast));
}

/**
 * The refusals a save answers by PICKING one of its candidates (deterministic
 * filters F2/F3) — and, since element ids step 3 (KPR-238 P4), an element
 * name several elements answer to (`ambiguous_element`), whose pick is sent
 * back as `elementChoices` rather than written into the text
 * (utils/elementRefusal.js).
 */
export const PICKABLE_REFUSALS = new Set(['lookup_needs_item', 'ambiguous_card', 'ambiguous_set', 'ambiguous_choice', 'ambiguous_element']);

/**
 * The CHOICE field (an element's list, not `card` / `set` / a facet) an
 * `ambiguous_choice` refusal is about. The API names no element for it
 * (keepr-api utils/kql/resolveNames.js: `{ field, value, candidates }`); its
 * candidates are the values the label stands for on ONE element, across
 * every card that declares it — so the element is the one whose list stores
 * a candidate. The page's field holds one declaring card's list (cards that
 * share an element name share a field), so it stores at least one candidate,
 * not necessarily the one picked (the F3 reviews' F5, N1).
 */
const lower = value => String(value ?? '').trim().toLowerCase();
const isChoiceList = field => !!field && !field.unknown && !field.reference && !field.system
	&& fieldKindOf(field) === 'choice' && Array.isArray(field.options?.choices);
const choiceValueOf = choice => (choice && typeof choice === 'object' ? choice.value ?? choice.label : choice);
const candidateValuesOf = refusal => new Set((Array.isArray(refusal?.candidates) ? refusal.candidates : [])
	.map(candidate => lower(candidate?.value)).filter(Boolean));
const isRefusedList = (field, candidates) => isChoiceList(field)
	&& field.options.choices.some(choice => candidates.has(lower(choiceValueOf(choice))));

/**
 * One refusal's candidates as `[{ value, label }]` — what a pick writes and
 * what the button says:
 *   lookup_needs_item   a record the title matches (`itemId`, its title)
 *   ambiguous_card      a card (`cardId`, its name)
 *   ambiguous_set       an element set (`setId`, its name)
 *   ambiguous_choice    a choice's stored value — said with the list it is
 *                       on when `fields` knows it ("Open · Status: open"),
 *                       since every candidate shares the label
 *   ambiguous_element   an element id, by its label and card ("Status on
 *                       Task"), then "Any of these cards" — every id, the
 *                       element group (`value` is then a list; `key` keys it)
 */
export function refusalChoices(refusal, { fields = null } = {}) {
	const list = Array.isArray(refusal?.candidates) ? refusal.candidates : [];
	switch (refusal?.code) {
		case 'ambiguous_element': return elementPickOptions(elementRefusalOf(refusal));
		case 'lookup_needs_item': return list.filter(c => c?.itemId).map(c => ({ value: String(c.itemId), label: c.title || String(c.itemId) }));
		case 'ambiguous_card': return list.filter(c => c?.cardId).map(c => ({ value: String(c.cardId), label: c.name || String(c.cardId) }));
		case 'ambiguous_set': return list.filter(c => c?.setId).map(c => ({ value: String(c.setId), label: c.name || String(c.setId) }));
		case 'ambiguous_choice': {
			const candidates = candidateValuesOf(refusal);
			const field = (fields || []).find(candidate => isRefusedList(candidate, candidates));
			return list.filter(c => c?.value !== undefined && c?.value !== null).map((c) => {
				const value = String(c.value);
				return { value, label: field ? `${field.shortLabel || field.label}: ${value}` : value };
			});
		}
		default: return [];
	}
}

/**
 * The query with the value a save was refused for replaced by the one the
 * person picked: the title a lookup or facet compared (`lookup_needs_item`
 * names the element — a facet as `@<key>`), a card or set key two tiers
 * share (on `card` / `set`, or a facet's own key), a choice label two lists
 * share — only on a one-element comparison whose list (`fields`) stores one
 * of the refusal's candidates, never a text search, a tag or a hop that
 * happens to hold the same word; without `fields`
 * nothing is moved for a choice. Compared case-insensitively, as the API
 * reads a name. Text that does not parse, or holds nothing to replace, comes
 * back as given.
 */
export function answerRefusal(query, refusal, picked, { fields = null } = {}) {
	const text = (query || '').trim();
	if (!text || !refusal || !PICKABLE_REFUSALS.has(refusal.code) || !picked) return query;
	// An element's pick is not written here: it goes back as `elementChoices`,
	// and the server writes the id, or the element group, itself.
	if (refusal.code === 'ambiguous_element') return query;
	let ast;
	try { ast = parse(text); } catch (err) { return query; }
	const wanted = String(refusal.value ?? '').trim().toLowerCase();
	const sameValue = value => value && value.type === 'str' && String(value.v).trim().toLowerCase() === wanted;
	const swapValues = (node) => {
		if (!node.value) return node;
		if (node.value.type === 'list') return { ...node, value: { ...node.value, v: node.value.v.map(v => (sameValue(v) ? makeStr(String(picked)) : v)) } };
		return sameValue(node.value) ? { ...node, value: makeStr(String(picked)) } : node;
	};
	const element = String(refusal.element || '');
	const refusedValues = candidateValuesOf(refusal);
	const walk = (node) => {
		if (!node || typeof node !== 'object') return node;
		if (Array.isArray(node.args)) return { ...node, args: node.args.map(walk) };
		if (node.arg) return { ...node, arg: walk(node.arg) };
		if (node.op !== 'cmp' || !Array.isArray(node.path)) return node;
		const root = node.path.length === 1 ? String(node.path[0]) : null;
		switch (refusal.code) {
			case 'lookup_needs_item': {
				// A lookup COLUMN inside a repeating group's block (keepr-api
				// KPR-681): the refusal names the group (`element`) and the
				// column (`column`, `columnId`); the title is swapped there.
				if (isBlock(node)) {
					if (!refusal.column || node.facet || node.path.join('.') !== element) return node;
					const columns = new Set([String(refusal.column), String(refusal.columnId || '')].filter(Boolean));
					const inside = (inner) => {
						if (!inner || typeof inner !== 'object') return inner;
						if (Array.isArray(inner.args)) return { ...inner, args: inner.args.map(inside) };
						if (inner.arg) return { ...inner, arg: inside(inner.arg) };
						return inner.op === 'cmp' && Array.isArray(inner.path) && inner.path.length === 1 && columns.has(String(inner.path[0])) ? swapValues(inner) : inner;
					};
					return { ...node, value: { ...node.value, v: inside(blockOf(node)) } };
				}
				// A system field (`card`, `set`, …) is never the element the
				// refusal names, even when an element shares its name.
				if (!node.facet && systemFieldOf(node)) return node;
				const named = node.facet ? `@${node.path[0]}` : node.path.join('.');
				return named === element ? swapValues(node) : node;
			}
			case 'ambiguous_card':
				if (node.facet && root && root.toLowerCase() === wanted) return { ...node, path: [String(picked), ...node.path.slice(1)] };
				return !node.facet && !node.forceElement && root === 'card' ? swapValues(node) : node;
			case 'ambiguous_set':
				return !node.facet && !node.forceElement && root === 'set' ? swapValues(node) : node;
			case 'ambiguous_choice':
				// One element, by name: the API never refuses a dotted hop.
				if (node.facet || node.path.length !== 1 || systemFieldOf(node)) return node;
				return isRefusedList(findField(fields || [], node.path, !!node.forceElement, false), refusedValues) ? swapValues(node) : node;
			default:
				return node;
		}
	};
	return serialize(walk(ast));
}
